import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Country } from "country-state-city";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  createPaymentSession,
  getPaymentStatus,
  getPlans,
  saveAuthSession,
  signupUser,
  submitPayment,
  validateIban,
} from "../services/authApi";
import InternationalPhoneInput from "../components/InternationalPhoneInput";

const fallbackPlans = [
  {
    id: "fallback-basic",
    name: "Basic",
    description:
      "Access to Basic Courses\nMobile-friendly access on any device\nCancel Anytime",
    price: 29,
  },
  {
    id: "fallback-standard",
    name: "Standard",
    description:
      "Access to Advanced Courses\nSupports access on multiple devices\nLimited Downloads\nLimited Templates\nCancel Anytime",
    price: 59,
  },
  {
    id: "fallback-premium",
    name: "Premium",
    description:
      "Access to All Courses\nUnlimited simultaneous devices\nUnlimited Downloads\nUnlimited Templates & Calendars\nIncludes priority support\nCancel Anytime",
    price: 99,
  },
];

function formatPrice(price) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(price || 0));
}

function normalizePlan(plan) {
  return {
    ...plan,
    priceLabel: formatPrice(plan.price),
    features: String(plan.description || "")
      .split("\n")
      .map((feature) => feature.trim())
      .filter(Boolean),
  };
}

function getNumericPlanId(planId) {
  const numericPlanId = Number(planId);
  return Number.isInteger(numericPlanId) && numericPlanId > 0
    ? numericPlanId
    : undefined;
}

function readFormValue(formData, key) {
  return String(formData.get(key) || "").trim();
}

function getStoredAuthUser() {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch (error) {
    return {};
  }
}

function getCheckoutUserDetails() {
  const user = getStoredAuthUser();
  const meta = user.user_meta || {};
  const nameParts = String(user.name || meta.display_name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return {
    firstName: user.firstname || meta.first_name || nameParts[0] || "",
    lastName:
      user.lastname ||
      meta.last_name ||
      (nameParts.length > 1 ? nameParts.slice(1).join(" ") : ""),
    email: user.email || "",
  };
}

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPlanId = location.state?.planId;
  const savedUserDetails = useMemo(() => getCheckoutUserDetails(), []);
  const [plans, setPlans] = useState(fallbackPlans.map(normalizePlan));
  const [billingCountryCode, setBillingCountryCode] = useState("CY");
  const [checkoutError, setCheckoutError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(10);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const data = await getPlans();
        setPlans(data.map(normalizePlan));
      } catch (error) {
        setPlans(fallbackPlans.map(normalizePlan));
      }
    };

    loadPlans();
  }, []);

  const selectedPlan = useMemo(() => {
    return (
      plans.find((plan) => String(plan.id) === String(selectedPlanId)) ||
      plans[0]
    );
  }, [plans, selectedPlanId]);

  const billingCountries = useMemo(() => {
    return Country.getAllCountries().sort((first, second) =>
      first.name.localeCompare(second.name)
    );
  }, []);

  const handleAutoDetectCountry = useCallback((country) => {
    setBillingCountryCode(country.isoCode);
  }, []);

  const redirectToProfile = useCallback(() => {
    navigate("/my-account", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!successModalOpen) {
      return undefined;
    }

    setRedirectCountdown(10);
    const intervalId = window.setInterval(() => {
      setRedirectCountdown((current) => {
        if (current <= 1) {
          window.clearInterval(intervalId);
          redirectToProfile();
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [redirectToProfile, successModalOpen]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const formData = new FormData(event.currentTarget);
    const firstName = readFormValue(formData, "first_name");
    const lastName = readFormValue(formData, "last_name");
    const email = readFormValue(formData, "email");
    const password = readFormValue(formData, "password");
    const phone =
      readFormValue(formData, "phone_full") || readFormValue(formData, "phone");
    const address = readFormValue(formData, "address");
    const city = readFormValue(formData, "city");
    const postalCode = readFormValue(formData, "postal_code");
    const country = readFormValue(formData, "country") || billingCountryCode;
    const iban = readFormValue(formData, "iban");
    const planId = getNumericPlanId(selectedPlan?.id);

    setCheckoutError("");
    setIsSubmitting(true);

    try {
      const ibanResult = await validateIban({ iban });

      if (ibanResult?.valid === false) {
        throw new Error(ibanResult.message || "Please enter a valid IBAN.");
      }

      if (!localStorage.getItem("authToken")) {
        if (!password) {
          throw new Error("Password is required to create your account.");
        }

        const authData = await signupUser({
          name: `${firstName} ${lastName}`.trim(),
          email,
          password,
          ...(planId ? { plan_id: planId } : {}),
          user_meta: {
            first_name: firstName,
            last_name: lastName,
            phone,
            address,
            city,
            postal_code: postalCode,
            country,
          },
        });
        saveAuthSession(authData);
      }

      const session = await createPaymentSession({
        ...(planId ? { plan_id: planId } : {}),
        plan_name: selectedPlan?.name || "Standard",
        amount: Number(selectedPlan?.price || 0),
        first_name: firstName,
        last_name: lastName,
        email,
        country,
        address,
        city,
        postal_code: postalCode,
        phone,
      });

      const sessionToken = session?.sessionToken;

      if (!sessionToken) {
        throw new Error("Payment session was not created.");
      }

      await submitPayment({
        session_token: sessionToken,
        iban,
        iban_holder_name: `${firstName} ${lastName}`.trim(),
      });

      try {
        await getPaymentStatus(sessionToken);
      } catch (error) {
        // The payment request was already accepted. Do not block library access
        // only because the provider status has not settled yet.
      }

      localStorage.setItem("paymentSubmitted", "true");
      setSuccessModalOpen(true);
    } catch (error) {
      setCheckoutError(error.message || "Unable to complete checkout.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen font-sans text-white bg-[#0F172A] relative">
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=2400"
          alt="Secure checkout"
          className="w-full h-full object-cover opacity-[0.04] mix-blend-screen"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/85 via-[#0F172A]/95 to-[#0F172A]" />
        <div className="absolute top-1/4 left-1/4 w-[560px] h-[560px] bg-[#2563EB]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[480px] h-[480px] bg-[#F43F5E]/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-50">
        <Header />
      </div>

      <form className="main" onSubmit={handleSubmit}>
        <div className="container checkout-page-wrap">
          <div className="checkout-panel-card">
            <div className="details-card">
              <h1 className="font-bold checkout-title">
                Complete Payment <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">for {selectedPlan?.name || "Standard"}</span>
              </h1>
              <p className="checkout-note">
                Server-to-server checkout: validate your IBAN and payment will
                be submitted automatically.
              </p>
              <h2 className="font-bold checkout-title">Your Information</h2>
              <div className="billing-details-fields">
                <div className="form-row">
                  <div className="form-group full">
                    <label>
                      First name <span>*</span>
                    </label>
                    <input
                      name="first_name"
                      type="text"
                      placeholder="John"
                      defaultValue={savedUserDetails.firstName}
                      required
                    />
                  </div>
                  <div className="form-group full">
                    <label>
                      Last name <span>*</span>
                    </label>
                    <input
                      name="last_name"
                      type="text"
                      placeholder="Doe"
                      defaultValue={savedUserDetails.lastName}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group full">
                    <label>
                      Email <span>*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      defaultValue={savedUserDetails.email}
                      required
                    />
                  </div>
                  <div className="form-group full">
                    <label>
                      Password <span>*</span>
                    </label>
                    <span className="password-input">
                      <input
                        type="password"
                        placeholder="Password"
                        name="password"
                        id="account_password"
                        minLength="6"
                      />
                      <button
                        type="button"
                        className="show-password-input"
                        aria-label="Show password"
                        aria-describedby="account_password"
                      />
                    </span>
                  </div>
                </div>

                <InternationalPhoneInput
                  onAutoDetectCountry={handleAutoDetectCountry}
                />
              </div>
              <h2 className="font-bold checkout-title">Your Billing Details</h2>
              <div className="billing-details-fields">
                <div className="form-row">
                  <div className="form-group full">
                    <label>
                      Street address <span>*</span>
                    </label>
                    <input name="address" type="text" required />
                  </div>
                  <div className="form-group full">
                    <label>
                      Town / City <span>*</span>
                    </label>
                    <input name="city" type="text" required />
                  </div>
                </div>
                <div className="form-row bottom-row">
                  <div className="form-group postcode">
                    <label>
                      Postcode / ZIP <span>*</span>
                    </label>
                    <input name="postal_code" type="text" required />
                  </div>
                  <div className="form-group country">
                    <label>
                      Country <span>*</span>
                    </label>
                    <div className="select-wrap">
                      <select
                        className="custom-select h-[57px]"
                        name="country"
                        value={billingCountryCode}
                        onChange={(event) =>
                          setBillingCountryCode(event.target.value)
                        }
                      >
                        {billingCountries.map((country) => (
                          <option key={country.isoCode} value={country.isoCode}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <h2 className="font-bold checkout-title">
                SEPA Direct Debit Information
              </h2>
              <div className="payment-box">
                <label className="payment-header">
                  <input type="radio" name="payment" id="sepaRadio" checked readOnly />
                  <div className="custom-radio" />
                  <span className="payment-title">SEPA Direct Debit</span>
                  <img src=".\sepa.svg" alt="SEPA" />
                </label>
                <div className="payment-content">
                  <p className="checkout-note">
                    Mandate Information.
                    <br /> By providing your IBAN and confirming this payment,
                    you are authorizing us to send instructions to your bank to
                    debit your account and your bank to debit your account in
                    accordance with those instructions. You are entitled to a
                    refund from your bank under the terms and conditions of your
                    agreement with your bank. A refund must be claimed within 8
                    weeks starting from the date on which your account was
                    debited.
                  </p>
                  <div className="checkout-fields">
                    <label>
                      IBAN <span>*</span>
                    </label>
                    <input
                      type="text"
                      name="iban"
                      placeholder="IBAN (e.g. DE89370400440532013000)"
                      autoComplete="off"
                      className="checkout-input"
                      defaultValue=""
                      required
                    />
                  </div>
                  <h2 className="font-bold checkout-title">
                    Payment Information
                  </h2>
                  <p className="mb-0 checkout-note">
                    All transactions are secure and encrypted. Credit card
                    information is never stored on our servers.
                  </p>
                  {checkoutError && (
                    <p className="checkout-error-message">{checkoutError}</p>
                  )}
                </div>
              </div>
              <p className="mt-3 text-[#94a3b8] text-center font-regular">
                Your personal data will be used to process your order, support
                your experience throughout this website, and for other purposes
                described in our privacy policy .
              </p>
              <div className="place-btn-wrapper">
                <button
                  type="submit"
                  className="place-order py-3.5 px-6 rounded-xl font-bold text-sm bg-[#2563EB] text-white hover:bg-[#1d4ed8] hover:shadow-[0_0_15px_rgba(37,99,235,0.4)] transition-all duration-300 flex items-center justify-center gap-2 group"
                  name="place_order"
                  id="place_order"
                  value={isSubmitting ? "Processing..." : "Complete Your Order Now"}
                  data-value="Complete Your Order Now"
                  disabled={isSubmitting || successModalOpen}
                >
                  {isSubmitting ? "Processing..." : "Complete Your Order Now"}
                </button>
              </div>
              <div className="sepa-icon">
                <img
                  src=".\SEPA Logo PNG Vector (SVG) Free Download.jpg"
                  alt="SEPA"
                />
              </div>
              <div className="checkout-features">
                <div className="feature-item">
                  <div className="feature-title">
                    <img src=".\time.svg" alt="" />
                  </div>
                  <div className="feature-text text-[#cbd5e1]">
                    <strong>Cancel Anytime</strong>
                    Cancel anytime, see T&amp;Cs for more info
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-title">
                    <img src=".\lock.svg" alt="" />
                  </div>
                  <div className="feature-text text-[#cbd5e1]">
                    <strong>Secure Processing</strong>
                    Encrypted, secure order processing
                  </div>
                </div>
              </div>
            </div>
            <div className="subscription-card">
              <h2>Subscription Information</h2>
              <div className="subscription-item">
                <div className="product-image">
                  <img src=".\icon.jpg" alt="product" />
                </div>
                <div className="product-info">
                  <div className="top-row">
                    <h3>{selectedPlan?.name || "Standard"} Monthly Subscription -</h3>
                    <span className="price">{selectedPlan?.priceLabel || "€59"}</span>
                  </div>
                  <p>Rebills every month, with a 3-day free trial</p>
                  <div className="font-light billing-info text-[#94a3b8] text-[14px]">
                    <span>Next Billing:</span>
                    <span>2026-06-11</span>
                  </div>
                </div>
              </div>

              <div className="summary-row">
                <span>Subtotal</span>
                <span>{selectedPlan?.priceLabel || "€59"}</span>
              </div>
              <div className="divider"></div>
              <div className="total-row">
                <span>Total</span>
                <span>{selectedPlan?.priceLabel || "€59"}</span>
              </div>

              <div className="absolute -right-12 -top-12 w-48 h-48 bg-[#2563EB]/10 rounded-full blur-[50px] pointer-events-none"></div>
            </div>
          </div>
        </div>
      </form>

      {successModalOpen && (
        <div className="checkout-success-overlay" role="dialog" aria-modal="true">
          <div className="checkout-success-modal">
            <h2>Payment Submitted</h2>
            <p>
              Your payment request has been received. The amount will be
              deducted from your IBAN within 2-3 business days.
            </p>
            <strong>
              Redirecting you to your profile in {redirectCountdown}
            </strong>
            <button type="button" onClick={redirectToProfile}>
              OK
            </button>
          </div>
        </div>
      )}

      <div className="relative z-10 mt-auto">
        <Footer />
      </div>
    </div>
  );
}
